import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';
import { ConfirmationDialog } from '../confirmation-dialog';

export type RouterPromptProps = {
  when: boolean;
};

/**
 * RouterPrompt is a functional React component that provides a confirmation prompt
 * when attempting to navigate away from the current page while unsaved changes exist.
 *
 * This component leverages the `history.block` method to intercept navigation attempts
 * and display a confirmation dialog based on the specified `when` condition.
 *
 * The dialog can present a custom title and description, and it provides handlers for
 * confirming or canceling the navigation.
 */
export const RouterPrompt: FC<RouterPromptProps> = ({ when }) => {
  const [t] = useTranslation();
  const blocker = useBlocker(when);

  return blocker.state === 'blocked' ? (
    <ConfirmationDialog
      label={t('common.navigation.confirm-unsaved-changes-title')}
      title={t('common.navigation.confirm-unsaved-changes-title')}
      open={blocker.state === 'blocked'}
      onClose={blocker.reset}
      onConfirm={blocker.proceed}
      onCancel={blocker.reset}
      description={t('common.navigation.confirm-unsaved-changes')}
    />
  ) : null;
};